import { Typography, Grid, Box } from '@material-ui/core';
import {
  createSelectField,
  createTextField,
  createNumberFormatField,
  createDateField,
  createAutocompleteAddressField,
  createRadioField,
} from '@roc/ui/componentBuilder';
import BorrowerEntityStore from '../../../stores/documentForms/borrowerEntity/borrowerEntityStore';
import { observer } from 'mobx-react-lite';
import { borrowerStatesNames, borrowerStatesAbbreviation, borrowerEntityTypeOptions } from '@roc/feature-utils';
import BorrowerEntityGrid from './borrowerEntityGrid';
import BorrowerEntitySearch from './borrowerEntitySearch';
import { useEffect } from 'react';
import { BorrowerEntityAutomationStatusChip } from './borrowerEntityAutomationStatusChip';

interface BorrowerEntityProps {
  store: BorrowerEntityStore;
}

const operatingAgreementOptions = [
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
];

const stateOptions = borrowerStatesAbbreviation.map((abbreviation, i) => ({
  value: abbreviation,
  label: borrowerStatesNames[i],
}));

const BorrowerEntity = observer((props: BorrowerEntityProps) => {
  const { store } = props;
  const { globalStore } = store;
  const { isInternalPortal } = globalStore;

  const handleAddressChange = event => {
    const { value, fullInfo } = event?.target || {};
    const { address_components, geometry } = fullInfo || {};
    store.saveAddressField(value, address_components, geometry);
  };

  const fields = store.form.fields;
  useEffect(() => {
    store.updateAddressFromFields();
  }, [
    fields.streetNumber.value,
    fields.streetName.value,
    fields.addressLine2.value,
    fields.city.value,
    fields.state.value,
    fields.zip.value,
  ]);

  useEffect(() => {
    store.fetchBorrowerEntityAutomationStatus();
  }, [store.borrowerEntity]);

  const disabled = store.automatedReadOnlyForm;

  return (
    <Grid container spacing={3} style={{
      width: '100%'
    }}>
      <Grid item xs={12}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5" style={{ marginRight: 8 }}>
            Entity Information
          </Typography>
          {isInternalPortal && (
            <BorrowerEntityAutomationStatusChip
              properties={{
                label: store.automationStatusMappingValue,
                status: store.automationStatus,
              }}
            />
          )}
        </Box>
      </Grid>
      <Grid item xs={12}>
        <BorrowerEntitySearch store={store} disabled={disabled} />
      </Grid>
      <Grid item xs={12} md={4}>
        {createSelectField({
          store,
          testId: 'type',
          fieldName: 'type',
          label: 'Type',
          options: borrowerEntityTypeOptions,
          disabled: disabled,
        })}
      </Grid>
      <Grid item xs={12} md={4}>
        {createNumberFormatField({
          store,
          testId: 'ein',
          fieldName: 'ein',
          label: 'EIN',
          format: '##-#######',
          disabled: disabled,
        })}
      </Grid>
      <Grid item xs={12} md={4}>
        {createAutocompleteAddressField({
          store,
          testId: 'address',
          fieldName: 'address',
          label: 'Entity Address',
          onChange: handleAddressChange,
          disabled: disabled,
        })}
      </Grid>
      <Grid item xs={12} md={4}>
        {createTextField({
          store,
          testId: 'streetNumber',
          fieldName: 'streetNumber',
          label: 'Street Number',
          disabled: disabled,
        })}
      </Grid>
      <Grid item xs={12} md={4}>
        {createTextField({
          store,
          testId: 'streetName',
          fieldName: 'streetName',
          label: 'Street Name',
          disabled: disabled,
        })}
      </Grid>
      <Grid item xs={12} md={4}>
        {createTextField({
          store,
          testId: 'addressLine2',
          fieldName: 'addressLine2',
          label: 'Apt/Unit/Suite/Other',
          disabled: disabled,
        })}
      </Grid>
      <Grid item xs={12} md={4}>
        {createSelectField({
          store,
          testId: 'state',
          fieldName: 'state',
          label: 'State',
          options: stateOptions,
          disabled: disabled,
        })}
      </Grid>
      <Grid item xs={12} md={4}>
        {createTextField({
          store,
          testId: 'city',
          fieldName: 'city',
          label: 'City',
          disabled: disabled,
        })}
      </Grid>
      <Grid item xs={12} md={4}>
        {createTextField({
          store,
          testId: 'zip',
          fieldName: 'zip',
          label: 'Zip Code',
          disabled: disabled,
        })}
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5">Entity Details</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        {createDateField({
          store,
          testId: 'cogsDate',
          fieldName: 'cogsDate',
          label: 'COGS Date',
          disabled: disabled,
        })}
      </Grid>
      <Grid item xs={12} md={4}>
        {createSelectField({
          store,
          testId: 'jurisdictionState',
          fieldName: 'jurisdictionState',
          label: 'Jurisdiction State',
          options: stateOptions,
          disabled: disabled,
        })}
      </Grid>
      <Grid item xs={12} md={4}>
        {createDateField({
          store,
          testId: 'operatingAgreementDate',
          fieldName: 'operatingAgreementDate',
          label: 'Date of Incorporation',
          disabled: disabled,
        })}
      </Grid>
      <Grid item xs={12} md={4}>
        {createRadioField({
          store,
          testId: 'operatingAgreement',
          fieldName: 'operatingAgreement',
          label: 'Operating Agreement',
          options: operatingAgreementOptions,
          row: true,
          disabled,
        })}
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5">Child Entity</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        {createTextField({
          store,
          testId: 'childEntityName',
          fieldName: 'childEntityName',
          label: 'Child Entity Name',
        })}
      </Grid>
      <Grid item xs={12} md={4}>
        {createTextField({
          store,
          testId: 'childEntityAddress',
          fieldName: 'childEntityAddress',
          label: 'Child Entity Address',
        })}
      </Grid>
      <Grid item xs={12} md={4}>
        {createTextField({
          store,
          testId: 'childEntityRole',
          fieldName: 'childEntityRole',
          label: 'Child Entity Role',
        })}
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5">Borrowers/Guarantors</Typography>
      </Grid>
      <Grid item xs={12}>
        <BorrowerEntityGrid store={store} />
      </Grid>
    </Grid>
  );
});

export default BorrowerEntity;
